import { Button, Dropdown, Form, Input, Menu, Select } from 'antd';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { gql } from '@apollo/client';
import _ from 'lodash';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';

const Option = Select.Option;

const searchPatientsQuery = gql`
  query($filter: String!, $organizationId: ID!) {
    searchPatients(filter: $filter, organizationId: $organizationId) {
      id
      identifier
      givenName1
      givenName2
      givenName3
      familyName
      dob
      sex
      address1
      address2
      city
      state
      postalCode
      phone
      email
    }
  }
`;

interface Patient {
  id: string;
  identifier?: string;
  givenName1?: string;
  givenName2?: string;
  givenName3?: string;
  familyName?: string;
  dob?: string;
  sex?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  email?: string;
}

interface Data {
  searchPatients: Patient[];
}

interface Variables {
  filter: string;
  organizationId: string;
}

interface Props {
  handleClearNewPatient: () => void;
  newPatient?: Patient;
  form: WrappedFormUtils;
  organization: {
    id: string;
    useClickToSearchForPatients: boolean;
  };
  style?: React.CSSProperties;
  onChange?: (arg0: Patient) => void;
  preselectedPatient?: string;
}

const formatPatientDisplay = (patient: Patient) => {
  const formattedName = `${patient.givenName1} ${patient.familyName}`;
  const formattedDob = patient.dob ? `${moment(patient.dob).format('MM/DD/YYYY')}` : '';
  const formattedMrn = patient.identifier ? `MRN: ${patient.identifier.slice(0, 15)}` : '';

  return {
    fullDisplay: `${formattedName} ${formattedDob} ${formattedMrn}`.trim(),
    name: formattedName,
    dob: formattedDob,
    mrn: formattedMrn,
  };
};

const PatientSearchField: React.FC<Props> = ({
  handleClearNewPatient,
  newPatient,
  form,
  organization,
  style,
  onChange,
}) => {
  const [options, setOptions] = useState<JSX.Element[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const client = useApolloClient();

  const createPatientOption = (patient: Patient): JSX.Element => {
    const { name, dob, mrn } = formatPatientDisplay(patient);
    return (
      <Option key={patient.id}>
        <div>
          {name}
          <span style={{ fontSize: 12, paddingLeft: 8 }}>{dob}</span>
          {mrn && <span style={{ fontSize: 12, color: '#aaa', float: 'right' }}>&nbsp;{mrn}</span>}
        </div>
      </Option>
    );
  };

  const handleSearch = async (patientName: string): Promise<void> => {
    handleClearNewPatient();
    const sanitizedPatientName = patientName.trim().replace(/\s+/g, ' ');
    if (sanitizedPatientName && sanitizedPatientName.length > 2) {
      const {
        data: { searchPatients: patients },
      } = await client.query<Data, Variables>({
        query: searchPatientsQuery,
        variables: { filter: sanitizedPatientName, organizationId: organization.id },
      });

      const newOptions: JSX.Element[] = [];
      patients.forEach(patient => {
        newOptions.push(createPatientOption(patient));
      });

      setOptions(newOptions);
      setPatients(patients);
    } else {
      setOptions([]);
      setPatients([]);
    }
  };

  const debounceHandleSearch = _.debounce(handleSearch, 300);

  const { getFieldDecorator, getFieldError } = form;
  const displayOptions = newPatient ? createPatientOption(newPatient) : options;

  useEffect(() => {
    if (newPatient) {
      const { fullDisplay } = formatPatientDisplay(newPatient);
      setSearchText(fullDisplay);
    }
  }, [newPatient]);

  const handleOptionSelect = (patient: Patient) => {
    if (onChange) {
      onChange(patient);
    }

    const { fullDisplay } = formatPatientDisplay(patient);
    setSearchText(fullDisplay);
    setDropdownVisible(false);

    form.setFieldsValue({ patientSearchField: patient.id });
  };

  const handleClickSearch = async (): Promise<void> => {
    setIsLoading(true);
    await handleSearch(searchText);
    setDropdownVisible(true);
    setIsLoading(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();

    const val = e.target.value;
    setSearchText(val);
    setDropdownVisible(false);

    form.setFieldsValue({ patientSearchField: undefined });

    if (!val) {
      if (newPatient) {
        handleClearNewPatient();
      }
    }
  };

  const searchComponent = organization.useClickToSearchForPatients ? (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Input
          placeholder="Patient Name"
          allowClear
          value={newPatient ? formatPatientDisplay(newPatient).fullDisplay : searchText}
          onChange={handleInputChange}
          disabled={isLoading}
        />
        <Button
          type="primary"
          onClick={handleClickSearch}
          loading={isLoading}
          disabled={isLoading || searchText.length < 3}
        >
          Find Patient
        </Button>
      </div>

      <Dropdown
        visible={dropdownVisible}
        overlay={
          <Menu style={{ maxHeight: '300px', overflow: 'auto' }}>
            {patients.length > 0 ? (
              patients.map(patient => {
                const { name, dob, mrn } = formatPatientDisplay(patient);
                return (
                  <Menu.Item key={patient.id} onClick={() => handleOptionSelect(patient)}>
                    {name}
                    <span style={{ fontSize: 12, paddingLeft: 8 }}>{dob}</span>
                    {mrn && <span style={{ fontSize: 12, color: '#aaa', float: 'right' }}>&nbsp;{mrn}</span>}
                  </Menu.Item>
                );
              })
            ) : (
              <Menu.Item disabled>No patients found</Menu.Item>
            )}
          </Menu>
        }
        trigger={['click']}
        onVisibleChange={visible => setDropdownVisible(visible)}
      >
        <div style={{ position: 'absolute', left: 0, right: 0 }} />
      </Dropdown>
    </div>
  ) : (
    <Select
      showSearch
      placeholder="Patient Name"
      style={style}
      defaultActiveFirstOption={false}
      showArrow={false}
      filterOption={false}
      onSearch={debounceHandleSearch}
      onChange={value => {
        const patient = patients.find(p => p?.id === value);
        if (patient && onChange) {
          onChange(patient);
        }
      }}
      notFoundContent={null}
    >
      {displayOptions}
    </Select>
  );

  const defaultHelpText = 'Type at least 3 letters to search';
  const error = getFieldError('patientSearchField');

  let helpProps = {};

  if (organization.useClickToSearchForPatients) {
    const helpContent = <div style={{ paddingBottom: 8 }}>{error ? error[0] : defaultHelpText}</div>;

    helpProps = {
      help: helpContent,
      validateStatus: error ? 'error' : '',
    };
  } else {
    helpProps = {
      help: defaultHelpText,
    };
  }

  return (
    <Form.Item label="What's the patient's name?" {...helpProps}>
      {getFieldDecorator('patientSearchField', {
        validateTrigger: organization.useClickToSearchForPatients ? 'onChange' : 'onBlur',
        rules: [
          {
            required: true,
            message: 'Please click "Find Patient".',
          },
        ],
      })(searchComponent)}
    </Form.Item>
  );
};

export default PatientSearchField;
